// src/data/adminApi.js
// Talks to the admin backend (admin-api/handler.mjs).
// On localhost it uses the local test server; on the live site, the Lambda.

// Paste the function URL printed by admin-api/setup.ps1 here.
const LIVE_URL = 'PASTE_ADMIN_FUNCTION_URL_HERE';

const API_URL =
  typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:4000' : LIVE_URL;

const TOKEN_KEY = 'pfr_admin_token';

export function getToken() {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch (e) {
    return null;
  }
}

export function signOut() {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch (e) {
    /* nothing to clear */
  }
}

async function call(payload) {
  let res;
  try {
    res = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    throw new Error(
      API_URL.startsWith('http://localhost')
        ? 'Local admin server is not running. Start it with: node admin-api/local-server.mjs'
        : 'Could not reach the server. Check your connection and try again.'
    );
  }
  const data = await res.json().catch(() => ({}));
  if (res.status === 401 && payload.action !== 'login') signOut();
  if (!res.ok) {
    const err = new Error(data.error || `Request failed (${res.status}).`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export async function signIn(password) {
  const { token } = await call({ action: 'login', password });
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
  } catch (e) {
    throw new Error('Your browser is blocking storage, so you cannot stay signed in.');
  }
}

export const saveCars = (cars) => call({ action: 'save', token: getToken(), cars });

// Shrinks a phone photo to at most 1600px wide before upload
function resize(file, maxWidth = 1600) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxWidth / img.naturalWidth);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (webp) => {
          // Some Safari versions cannot make WebP; fall back to JPEG
          if (webp && webp.type === 'image/webp') return resolve(webp);
          canvas.toBlob((jpg) => (jpg ? resolve(jpg) : reject(new Error('Could not read that photo.'))), 'image/jpeg', 0.85);
        },
        'image/webp',
        0.85
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file is not a photo this browser can read.'));
    };
    img.src = url;
  });
}

export async function uploadPhoto(file) {
  const blob = await resize(file);
  const data = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = () => reject(new Error('Could not read that photo.'));
    reader.readAsDataURL(blob);
  });
  const { url } = await call({ action: 'upload', token: getToken(), type: blob.type, data });
  return url;
}
